import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import useAuth from 'hooks/useAuth.ts';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetEmployeeWebQuery } from 'store/api/employeeWeb';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { useGetSystemRolesListQuery } from 'store/api/systemRoles';
import { EmployeeForm } from 'types/employees';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import EmployeePageForm from './EmployeePageForm';
import {
    EMPLOYEE_FORM_CLASSIFIERS,
    EmployeeSectionId,
    employeeDtoToForm,
    employeeSectionGates,
    firstEditableEmployeeCard,
} from './employeeFormMapping';
import { useEmployeePermissions } from './useEmployeePermissions';

const ClassicEmployeePageEdit = () => {
    const { id: employeeId } = useParams();
    const { data: systemRoleList, isLoading: isLoadingSystemRoles } = useGetSystemRolesListQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EMPLOYEE_FORM_CLASSIFIERS });
    const { user } = useAuth();
    const hasPermissionToUpdateEmployees = useHasPermissions(['UPDATE.EMPLOYEES']);
    const isEditingCurrentUserAndHasPermissionToUpdateEmployees =
        hasPermissionToUpdateEmployees && employeeId === String(user?.id);

    const { data: employee, isLoading, isError, refetch } = useGetEmployeeByIdQuery(employeeId ?? skipToken);
    // The employee record carries no website picture; the Web integration section edits the web profile's.
    const { data: webDetail, isLoading: isLoadingWeb } = useGetEmployeeWebQuery(
        employee?.web && employeeId ? employeeId : skipToken
    );

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.EMPLOYEE,
        fields: employee?.customFields,
    });

    // Set initial form data (derived from RTK Query employee + classifiers; render-time compute).
    const initialData = useMemo<EmployeeForm | undefined>(() => {
        if (isLoadingCustomFieldData || isLoadingClassifiers || isLoadingWeb) {
            return undefined;
        }
        if (employee) {
            return {
                ...employeeDtoToForm(employee, { classifiers, customFieldValues: initialCustomFieldsObject }),
                webPictureBase64: webDetail?.pictureBase64 ?? null,
            };
        }

        return undefined;
    }, [
        employee,
        webDetail,
        initialCustomFieldsObject,
        classifiers,
        isLoadingCustomFieldData,
        isLoadingClassifiers,
        isLoadingWeb,
    ]);

    if (
        isLoading ||
        isLoadingSystemRoles ||
        (initialData === undefined && !isError) ||
        isLoadingCustomFieldData ||
        isLoadingClassifiers
    ) {
        return <Loader />;
    }

    return (
        <EmployeePageForm
            type="edit"
            initialData={initialData}
            onVacation={employee?.onVacation}
            systemRoles={systemRoleList}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={customFields}
            isEditingCurrentUserAndHasPermissionToUpdateEmployees={
                isEditingCurrentUserAndHasPermissionToUpdateEmployees
            }
        />
    );
};

/** In the new design the employee edits card by card on its page: this lands there, the first card the user may edit open. */
const EmployeePageEdit = () => {
    const isNew = useIsNewDesign();
    const { id } = useParams();
    const permissions = useEmployeePermissions(id);
    const shownCards: Partial<Record<EmployeeSectionId, boolean>> = {
        targets: useIsFeatureEnabled(FeatureFlag.DASHBOARD_PROGRESS_TRACKING),
        'peer-group': useIsFeatureEnabled(FeatureFlag.PARTNER_PEER_GROUP),
        'web-integration': useIsFeatureEnabled(FeatureFlag.WEB_TAB_EMPLOYEES),
    };
    if (!isNew) {
        return <ClassicEmployeePageEdit />;
    }
    const first = firstEditableEmployeeCard(employeeSectionGates(permissions), shownCards);
    return <Navigate to={`/employee/${id}${first ? `?edit=${first}` : ''}`} replace />;
};

export default EmployeePageEdit;
